// Cave84 — Add-Bottle graph orchestrator (redesign)
// Spec §3.1 + §4.1. Single entry point for the redesigned single-screen Add Bottle flow.
//
// Design decision (approved 2026-07-03): ALL Firestore writes are deferred to submit.
// Nothing is written while the user types — the Producer→Appellation→Wine→Vintage→Bottle
// graph is built from local form state and persisted only here, in dependency order.
// This eliminates the orphan-producer bug from the old modal flow (which fired
// createProducer immediately) and fixes the invalid `classification_level: 'Regional'`
// casing by taking a properly-typed ClassificationLevel from the caller.
//
// Writes are dependent (each entity needs the previous entity's id) so they run
// sequentially. Each underlying service already batches its own multi-doc writes and
// keeps denormalized counters + generation-queue side effects intact.

import { Timestamp } from 'firebase/firestore';
import type {
  BottleFormat, ClassificationLevel, Currency, FillLevel, LabelCondition,
  PurchaseSource, Vintage, WineStyle, WineSubStyle
} from '@cave84/shared-types';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from './firebase';
import { cuveeKey, producerKey } from '@/utils/catalogKeys';
import { createProducer } from './producers';
import { createAppellation } from './appellations';
import { createWine, type WineCreateInput } from './wines';
import { createVintage, findVintageByWineAndYear } from './vintages';
import { createBottles, type BottleCreateInput } from './bottles';

// ---------------------------------------------------------------------------
// Reference types — each entity is EITHER an existing id OR a spec to create
// ---------------------------------------------------------------------------

export type ProducerRef =
  | { kind: 'existing'; id: string }
  | { kind: 'new'; name: string; country: string; region: string; subregion?: string };

export type AppellationRef =
  | { kind: 'existing'; id: string }
  | {
      kind: 'new';
      name: string;
      classification_level: ClassificationLevel;
      country: string;
      region: string;
      subregion?: string;
    };

export interface NewWineSpec {
  producer: ProducerRef;
  appellation: AppellationRef;
  cuvee_name?: string;
  vineyard_specific?: string;
  style: WineStyle;
  sub_style?: WineSubStyle;
  grape_composition: { grape: string; pct: number }[];
}

export type WineRef =
  | { kind: 'existing'; id: string }
  | { kind: 'new'; spec: NewWineSpec };

export interface BottleSpec {
  format: BottleFormat;
  fill_level: FillLevel;
  label_condition: LabelCondition;
  capsule_condition: LabelCondition;
  in_OWC: boolean;
  is_special_occasion: boolean;
  special_occasion_reason?: string;
  notes?: string;
  purchase_date: Date;
  purchase_source: PurchaseSource;
  purchase_source_detail?: string;
  cost_landed: number;
  cost_currency: Currency;
  cost_eur_at_purchase: number;
  current_cellar_id: string;
  current_location_id?: string;
}

export interface AddBottleFlowInput {
  wine: WineRef;
  year: number;
  bottle: BottleSpec;
  qty: number;
}

export interface AddBottleFlowResult {
  wineId: string;
  vintageId: string;
  bottleIds: string[];
  createdNewWine: boolean;
  createdNewVintage: boolean;
}

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

/** Conservative default drinking window for a manually-added vintage.
 *  Claude analysis refines this later; assessed_by marks it as a placeholder. */
function defaultDrinkingWindow(year: number): Vintage['drinking_window_initial'] {
  return {
    from_year: year + 3,
    peak_from: year + 6,
    peak_to: year + 12,
    to_year: year + 18,
    status: 'developing',
    assessed_at: Timestamp.now(),
    assessed_by: 'manual',
  };
}

function clean(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

// ---------------------------------------------------------------------------
// Orchestrator
// ---------------------------------------------------------------------------

async function resolveWine(wine: WineRef): Promise<{ wineId: string; createdNewWine: boolean }> {
  if (wine.kind === 'existing') {
    return { wineId: wine.id, createdNewWine: false };
  }

  const spec = wine.spec;

  // 1. Producer — reuse or create. Last line of defence against duplicates
  // (the form already warns): never create a producer whose name matches an
  // existing one under the shared rules — fail instead of guessing.
  let producerId: string;
  let producerName: string;
  if (spec.producer.kind === 'existing') {
    producerId = spec.producer.id;
    producerName = '';
  } else {
    const key = producerKey(spec.producer.name);
    const all = await getDocs(collection(db, 'producers'));
    const twins = all.docs.filter(d =>
      [d.get('name'), ...((d.get('aliases') as string[] | undefined) ?? [])].some(n => producerKey(String(n ?? '')) === key));
    if (twins.length > 0) {
      throw new Error(`Producer already exists as ${twins.map(d => `"${d.get('name')}"`).join(' / ')} — select it instead of creating "${spec.producer.name.trim()}".`);
    }
    producerName = spec.producer.name.trim();
    const created = await createProducer({
      name: spec.producer.name.trim(),
      aliases: [],
      country: spec.producer.country.trim().toUpperCase(),
      region: spec.producer.region.trim(),
      ...(clean(spec.producer.subregion) ? { subregion: clean(spec.producer.subregion)! } : {}),
      top_cuvees: [],
    });
    producerId = created.id;
  }

  // 2. Appellation — reuse or create
  let appellationId: string;
  if (spec.appellation.kind === 'existing') {
    appellationId = spec.appellation.id;
  } else {
    const name = spec.appellation.name.trim();
    const created = await createAppellation({
      name,
      hierarchy_path: name,
      classification_level: spec.appellation.classification_level,
      country: spec.appellation.country.trim().toUpperCase(),
      region: spec.appellation.region.trim(),
      ...(clean(spec.appellation.subregion) ? { subregion: clean(spec.appellation.subregion)! } : {}),
    });
    appellationId = created.id;
  }

  // 3. Duplicate-wine guard for an existing producer (same rules as the form).
  if (spec.producer.kind === 'existing') {
    const [prodSnap, winesSnap, appsSnap] = await Promise.all([
      getDocs(query(collection(db, 'producers'))),
      getDocs(query(collection(db, 'wines'), where('producer_id', '==', producerId))),
      getDocs(collection(db, 'appellations')),
    ]);
    producerName = String(prodSnap.docs.find(d => d.id === producerId)?.get('name') ?? '');
    const appName = new Map(appsSnap.docs.map(d => [d.id, String(d.get('name') ?? '')]));
    const key = cuveeKey(`${appName.get(appellationId) ?? ''} ${spec.cuvee_name ?? ''} ${spec.vineyard_specific ?? ''}`, [producerName]);
    const twin = winesSnap.docs.find(d => d.get('style') === spec.style
      && cuveeKey(`${appName.get(String(d.get('appellation_id'))) ?? ''} ${d.get('cuvee_name') ?? ''} ${d.get('vineyard_specific') ?? ''}`, [producerName]) === key);
    if (twin) {
      throw new Error(`This wine already exists as "${twin.get('cuvee_name') ?? '(no cuvée)'}" — select it instead of creating a duplicate.`);
    }
  }

  // 4. Wine — build without undefined keys (Firestore rejects undefined)
  const wineInput: WineCreateInput = {
    producer_id: producerId,
    appellation_id: appellationId,
    style: spec.style,
    grape_composition: spec.grape_composition,
    ...(clean(spec.cuvee_name) ? { cuvee_name: clean(spec.cuvee_name)! } : {}),
    ...(clean(spec.vineyard_specific) ? { vineyard_specific: clean(spec.vineyard_specific)! } : {}),
    ...(spec.sub_style ? { sub_style: spec.sub_style } : {}),
  };
  const createdWine = await createWine(wineInput);
  return { wineId: createdWine.id, createdNewWine: true };
}

export async function submitAddBottleFlow(input: AddBottleFlowInput): Promise<AddBottleFlowResult> {
  const { wine, year, bottle, qty } = input;

  const { wineId, createdNewWine } = await resolveWine(wine);

  // 4. Vintage — reuse existing (only possible for an existing wine) or create
  let vintageId: string;
  let createdNewVintage = false;
  const existingVintage = createdNewWine ? null : await findVintageByWineAndYear(wineId, year);
  if (existingVintage) {
    vintageId = existingVintage.id;
  } else {
    const window = defaultDrinkingWindow(year);
    const created = await createVintage({
      wine_id: wineId,
      year,
      vintage_quality_rating: 3,
      drinking_window_initial: window,
      drinking_window_current: window,
    });
    vintageId = created.id;
    createdNewVintage = true;
  }

  // 5. Bottles — bulk add (shared purchase lot), counters batched inside the service
  const bottleInput: BottleCreateInput = {
    vintage_id: vintageId,
    wine_id: wineId,
    format: bottle.format,
    purchase_date: bottle.purchase_date,
    purchase_source: bottle.purchase_source,
    ...(clean(bottle.purchase_source_detail) ? { purchase_source_detail: clean(bottle.purchase_source_detail)! } : {}),
    cost_landed: bottle.cost_landed,
    cost_currency: bottle.cost_currency,
    cost_eur_at_purchase: bottle.cost_eur_at_purchase,
    current_cellar_id: bottle.current_cellar_id,
    ...(clean(bottle.current_location_id) ? { current_location_id: clean(bottle.current_location_id)! } : {}),
    is_special_occasion: bottle.is_special_occasion,
    ...(clean(bottle.special_occasion_reason) ? { special_occasion_reason: clean(bottle.special_occasion_reason)! } : {}),
    fill_level: bottle.fill_level,
    label_condition: bottle.label_condition,
    capsule_condition: bottle.capsule_condition,
    in_OWC: bottle.in_OWC,
    ...(clean(bottle.notes) ? { notes: clean(bottle.notes)! } : {}),
  };
  const bottleIds = await createBottles(bottleInput, qty);

  return { wineId, vintageId, bottleIds, createdNewWine, createdNewVintage };
}
